import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import type { Uuid } from '@/lib/types';

/*
  ════════════════════════════════════════════════════════════════════════════
   현장 입력(직원) — 공정 실적 · 자재 전표 · 현장사진 · 공사일보
  ════════════════════════════════════════════════════════════════════════════

  서버는 `ExternalFieldController`. 쓰기 판정은 <b>웹의 메뉴 권한</b>이다(사용자 지시 2026-10-27
  「자료는 웹에서 부여된 권한대로 처리」) — 웹 컨트롤러가 거는 키·동작 그대로를 서버가 매 요청 다시 본다.
  여기 {@link useFieldGrants} 는 메뉴를 세우고 쓰기 버튼을 여는 데만 쓴다. 눌렀는데 막히면 서버가 답한다.
*/

/** 웹 메뉴 키 — 웹 컨트롤러의 `MENU` 상수와 같은 글자다. */
export type FieldMenu = 'site_pmis_progress' | 'site_pmis_material' | 'site_pmis_scenery' | 'site_pmis_daily';

export type FieldAction = 'view' | 'create' | 'edit' | 'delete';

type Grant = { menuKey: string; canView: boolean; canCreate: boolean; canEdit: boolean; canDelete: boolean };

/**
 * 직원의 메뉴 권한. 직원이 아니면 묻지 않고 모두 거짓이다 — 협력사·감리에게 이 입력은 없다.
 *
 * <p>아직 못 받았으면 거짓이다. 메뉴가 잠깐 늦게 서는 편이, 없는 권한으로 버튼이 잠깐 서는 것보다 낫다.
 */
export function useFieldGrants() {
  const session = useSession();
  const isStaff = session.data?.partyType === 'STAFF';
  const grants = useQuery({
    queryKey: ['staff', 'grants'] as const,
    queryFn: () => apiFetch<Grant[]>('/staff/grants'),
    enabled: isStaff,
    staleTime: 60_000,
  });

  // 응답이 바뀔 때만 새 함수다 — 사이드바가 이것을 memo 의 기준으로 쓴다.
  const data = grants.data;
  const can = useCallback((menuKey: string, action: FieldAction): boolean => {
    const row = data?.find((one) => one.menuKey === menuKey);
    if (row === undefined) return false;
    if (action === 'view') return row.canView;
    if (action === 'create') return row.canCreate;
    if (action === 'edit') return row.canEdit;
    return row.canDelete;
  }, [data]);

  return { can, ready: grants.isSuccess };
}

const fieldKeys = {
  wbsActuals: (siteId: Uuid) => ['site', siteId, 'wbs-actuals'] as const,
  materialOptions: (siteId: Uuid) => ['site', siteId, 'material-options'] as const,
  slips: (siteId: Uuid) => ['site', siteId, 'material-slips'] as const,
  scenery: (siteId: Uuid) => ['site', siteId, 'scenery'] as const,
  dailyReports: (siteId: Uuid) => ['site', siteId, 'construction-daily-reports'] as const,
  dailyReport: (siteId: Uuid, id: Uuid) => ['site', siteId, 'construction-daily-report', id] as const,
};

// ── 공정 실적 ───────────────────────────────────────────────────────────────

export type WbsActual = {
  itemId: Uuid;
  plannedQuantity: number | null;
  unit: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  /** 누계 실적물량. 그날 한 양이 아니다. */
  actualQuantity: number | null;
  completed: boolean;
};

export type WbsActualWrite = {
  actualStartDate: string | null;
  actualEndDate: string | null;
  actualQuantity: number | null;
};

export function useWbsActuals(siteId: Uuid, enabled: boolean) {
  return useQuery({
    queryKey: fieldKeys.wbsActuals(siteId),
    queryFn: () => apiFetch<WbsActual[]>(`/sites/${siteId}/wbs-actuals`),
    enabled: enabled && siteId !== '',
  });
}

export function useSaveWbsActual(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: Uuid; body: WbsActualWrite }) =>
      apiFetch<WbsActual>(`/sites/${siteId}/wbs-actuals/${itemId}`, { method: 'PUT', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.wbsActuals(siteId) });
      // 실적은 진척 스냅샷을 함께 남긴다(WbsService.saveActual) — 공정율 목록도 다시 받는다.
      void queryClient.invalidateQueries({ queryKey: ['site', siteId, 'progress'] });
    },
  });
}

// ── 자재 전표 ───────────────────────────────────────────────────────────────

export type MaterialOptions = {
  items: Array<{ id: Uuid; materialKind: string; materialKindLabel: string; name: string; spec: string | null; unit: string | null }>;
  zones: Array<{ id: Uuid; buildingId: Uuid | null; zoneName: string }>;
  buildings: Array<{ id: Uuid; name: string }>;
};

export type SlipKind = 'DELIVERED' | 'USED';

export type MaterialSlip = {
  id: Uuid;
  slipDate: string;
  materialName: string | null;
  spec: string | null;
  zoneName: string | null;
  buildingName: string | null;
  locationNote: string | null;
  slipKind: SlipKind;
  quantity: number | null;
  unit: string | null;
  vendorName: string | null;
  note: string | null;
  status: string;
  createdByName: string | null;
  fileCount: number;
};

export type SlipWrite = {
  slipDate: string;
  materialId: Uuid;
  zoneId: Uuid | null;
  locationNote: string | null;
  slipKind: SlipKind;
  quantity: number;
  vendorName: string | null;
  note: string | null;
};

export function useMaterialOptions(siteId: Uuid, enabled: boolean) {
  return useQuery({
    queryKey: fieldKeys.materialOptions(siteId),
    queryFn: () => apiFetch<MaterialOptions>(`/sites/${siteId}/material-options`),
    enabled: enabled && siteId !== '',
    staleTime: 5 * 60_000,
  });
}

export function useMaterialSlips(siteId: Uuid, from: string, to: string) {
  return useQuery({
    queryKey: [...fieldKeys.slips(siteId), from, to] as const,
    queryFn: () =>
      apiFetch<MaterialSlip[]>(`/sites/${siteId}/material-slips?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    enabled: siteId !== '',
  });
}

/**
 * 전표 한 장 + 송장. <b>줄을 먼저 세우고</b> 송장을 붙인다(웹·앱과 같은 순서). 송장이 실패해도 줄은 남긴다 —
 * 다시 치게 하는 것이 현장에서 가장 비싸다.
 */
export function useCreateSlip(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, invoice }: { body: SlipWrite; invoice: File | null }) => {
      const saved = await apiFetch<MaterialSlip>(`/sites/${siteId}/material-slips`, { method: 'POST', body });
      if (invoice !== null) {
        const form = new FormData();
        form.append('file', invoice, invoice.name);
        try {
          await apiFetch<MaterialSlip>(`/sites/${siteId}/material-slips/${saved.id}/files`, { method: 'POST', body: form });
        }
        catch {
          // 줄은 섰다. 송장은 웹에서 다시 붙일 수 있다.
        }
      }
      return saved;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fieldKeys.slips(siteId) }),
  });
}

export function useDeleteSlip(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiFetch<void>(`/sites/${siteId}/material-slips/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fieldKeys.slips(siteId) }),
  });
}

// ── 현장사진 ────────────────────────────────────────────────────────────────

export type SceneryKind = 'SCENERY' | 'CONSTRUCTION';

export type SceneryPhoto = {
  id: Uuid;
  fileName: string | null;
  caption: string | null;
  photoKind: SceneryKind;
  /** 그 주의 월요일 — 서버가 정한 값이다. */
  weekOf: string;
  takenAt: string | null;
};

export function useScenery(siteId: Uuid) {
  return useQuery({
    queryKey: fieldKeys.scenery(siteId),
    queryFn: () => apiFetch<SceneryPhoto[]>(`/sites/${siteId}/scenery`),
    enabled: siteId !== '',
  });
}

/** 한 장씩 올린다. 주차를 비우면 서버가 낸다(웹 업로드 창과 같은 규칙). */
export function useUploadScenery(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, photoKind, weekOf }: { file: File; photoKind: SceneryKind; weekOf: string | null }) => {
      const form = new FormData();
      form.append('file', file, file.name);
      form.append('photoKind', photoKind);
      if (weekOf !== null) form.append('weekOf', weekOf);
      return apiFetch<SceneryPhoto>(`/sites/${siteId}/scenery`, { method: 'POST', body: form });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fieldKeys.scenery(siteId) }),
  });
}

// ── 공사일보 ────────────────────────────────────────────────────────────────

export type DailyReportSummary = {
  id: Uuid;
  reportDate: string;
  weather: string | null;
  temperature: string | null;
  todayWork: string | null;
  photoCount: number;
  updatedAt: string | null;
  /** DRAFT 작성중 · IN_REVIEW 결재중 · COMPLETED 완료 · REJECTED 반려. */
  signStatus: string | null;
};

export type WorkLine = { name: string; wbsItemId: Uuid | null; today: string | null; tomorrow: string | null };

type ReportLine = { name: string; wbsItemId: Uuid | null };

export type DailyReport = {
  id: Uuid;
  siteId: Uuid;
  reportDate: string;
  weather: string | null;
  temperature: string | null;
  todayWork: string | null;
  tomorrowPlan: string | null;
  specialNote: string | null;
  labors: ReportLine[] | null;
  works: WorkLine[] | null;
  signStatus: string | null;
};

export type DailyReportText = {
  reportDate: string;
  weather: string | null;
  temperature: string | null;
  todayWork: string | null;
  tomorrowPlan: string | null;
  specialNote: string | null;
  /** null 이면 저장된 공정별 작업내용을 건드리지 않는다. */
  works: WorkLine[] | null;
};

export function useDailyReports(siteId: Uuid, from: string, to: string) {
  return useQuery({
    queryKey: [...fieldKeys.dailyReports(siteId), from, to] as const,
    queryFn: () =>
      apiFetch<DailyReportSummary[]>(
        `/sites/${siteId}/construction-daily-reports?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: siteId !== '',
  });
}

export function useDailyReport(siteId: Uuid, id: Uuid | null) {
  return useQuery({
    queryKey: fieldKeys.dailyReport(siteId, id ?? ''),
    queryFn: () => apiFetch<DailyReport>(`/sites/${siteId}/construction-daily-reports/${id}`),
    enabled: siteId !== '' && id !== null,
  });
}

export function useSaveDailyReport(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: Uuid | null; body: DailyReportText }) =>
      id === null
        ? apiFetch<DailyReport>(`/sites/${siteId}/construction-daily-reports`, { method: 'POST', body })
        : apiFetch<DailyReport>(`/sites/${siteId}/construction-daily-reports/${id}`, { method: 'PUT', body }),
    onSuccess: (saved) => {
      queryClient.setQueryData(fieldKeys.dailyReport(siteId, saved.id), saved);
      void queryClient.invalidateQueries({ queryKey: fieldKeys.dailyReports(siteId) });
    },
  });
}
